import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Radio, Users, Clock } from 'lucide-react';
import { RoomService, lifecycleStateOf } from '../../services/RoomService';
import {
    ROOM_MAX_MEMBERS,
    ROOM_MIN_MEMBERS,
    formatTimeRemaining,
    isFull,
} from '../../services/roomLifecycle';
import { EVENTS } from '../../constants/navigation';
import type { Room } from '../../types';

interface RoomShareCardProps {
    roomId: string;
    /** Captured when the yap was posted, so the card still says something once
     *  the room is gone. */
    title: string;
}

/**
 * A room advertised in the feed.
 *
 * The point is recruitment: a room is otherwise only findable by opening the
 * Rooms tab, which is a problem when it needs three people before its clock even
 * starts. Tapping through goes to the room, where joining still means requesting
 * a seat — the card is an advert, not a side door.
 *
 * The counts are read live rather than baked into the yap, because a room's
 * membership and remaining time change by the minute and a stale "2/15" would be
 * worse than none. The yap outlives the room, though — rooms are hard-deleted
 * within a day — so the ended state is the normal end state for this card, not
 * an error, and the title from the yap is what it falls back to.
 *
 * The unavailable state covers two cases deliberately, because the client cannot
 * tell them apart: the room was purged, or it belongs to the other age bracket
 * and the read rule refuses it. Yaps are not bracket-filtered, so a minor can see
 * an adult'''s advert; either way the room is not theirs to open, and one honest
 * line beats guessing which.
 */
export function RoomShareCard({ roomId, title }: RoomShareCardProps) {
    const [room, setRoom] = useState<Room | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!roomId) return;
        return RoomService.subscribeToRoom(roomId, (next) => {
            setRoom(next);
            setLoaded(true);
        });
    }, [roomId]);

    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(t);
    }, []);

    const ended = loaded && (!room || room.status === 'closed');
    const state = room ? lifecycleStateOf(room) : null;
    const full = state ? isFull(state.memberCount) : false;

    const open = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (ended) return;
        window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_ROOM, { detail: { id: roomId } }));
    };

    return (
        <motion.div
            whileTap={ended ? undefined : { scale: 0.98 }}
            onClick={open}
            className="w-full rounded-2xl flex items-center gap-3 p-3"
            style={{
                marginTop: 8,
                marginBottom: 8,
                background: ended ? 'rgba(127,127,127,0.10)' : 'rgba(68,120,234,0.10)',
                border: `1px solid ${ended ? 'rgba(127,127,127,0.20)' : 'rgba(68,120,234,0.28)'}`,
                cursor: ended ? 'default' : 'pointer',
                opacity: ended ? 0.75 : 1,
            }}
        >
            <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: ended ? 'rgba(127,127,127,0.15)' : 'rgba(68,120,234,0.18)' }}
            >
                <Radio className="w-5 h-5" style={{ color: ended ? '#8e8e8e' : '#4478ea' }} />
            </div>

            <div className="min-w-0 flex-1">
                <p
                    className="text-black dark:text-white truncate"
                    style={{ fontWeight: 700, fontSize: 14 }}
                >
                    {room?.title || title}
                </p>

                {ended ? (
                    <p className="text-[#666666] dark:text-[#a0a0a0]" style={{ fontSize: 12 }}>
                        This room is no longer available
                    </p>
                ) : !loaded || !state ? (
                    <p className="text-[#666666] dark:text-[#a0a0a0]" style={{ fontSize: 12 }}>
                        Loading room...
                    </p>
                ) : (
                    <div className="flex items-center gap-3 text-[#666666] dark:text-[#a0a0a0]" style={{ fontSize: 12 }}>
                        <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {state.memberCount}/{ROOM_MAX_MEMBERS}
                        </span>
                        <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {room?.status === 'lobby'
                                ? `Needs ${Math.max(0, ROOM_MIN_MEMBERS - state.memberCount)} more`
                                : formatTimeRemaining(state.expiresAt, now)}
                        </span>
                    </div>
                )}
            </div>

            {!ended && loaded && (
                <span
                    className="shrink-0 rounded-full"
                    style={{
                        fontSize: 11,
                        fontWeight: 700,
                        paddingTop: 5,
                        paddingBottom: 5,
                        paddingLeft: 10,
                        paddingRight: 10,
                        background: full ? 'rgba(231,76,60,0.15)' : '#4478ea',
                        color: full ? '#E74C3C' : '#fff',
                    }}
                >
                    {full ? 'Full' : 'Join'}
                </span>
            )}
        </motion.div>
    );
}

export default RoomShareCard;
